/**
 * PortfolioSwitcher — the portfolio selector promoted into the app shell
 * (handoff element F: a brand-tinted pill with a dot, the name, and a chevron).
 *
 * Reads the portfolio list from settings (no trades/prices fetched) and drives
 * the shared ActivePortfolio context, persisting to settings.activePortfolioId
 * so one selection follows the user across every page. Behaviour is identical
 * to the selector that used to live in the Overview hub header.
 */
import { ChevronDown } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { usePortfolioData } from '@/lib/usePortfolioData';
import { updateSettings } from '@/lib/settingsStore';
import { ALL_PORTFOLIOS_ID, ALL_PORTFOLIOS_LABEL, isAllPortfolios } from '@/lib/allPortfolios';

interface PortfolioSwitcherProps {
  variant?: 'desktop' | 'mobile';
  className?: string;
}

export function PortfolioSwitcher({ variant = 'desktop', className }: PortfolioSwitcherProps) {
  const { settings, activePortfolioId, setActivePortfolioId } = usePortfolioData({ trades: false, prices: false });

  const portfolioList = settings.portfolios ?? [];
  const isAll = isAllPortfolios(activePortfolioId);
  const portfolioName = isAll
    ? ALL_PORTFOLIOS_LABEL
    : (portfolioList.find((p) => p.id === activePortfolioId)?.name ?? 'Portfolio');

  if (portfolioList.length === 0 && !isAll) return null;

  const changePortfolio = (id: string) => {
    setActivePortfolioId(id);
    void updateSettings((s) => ({ ...s, activePortfolioId: id }));
  };

  const canSwitch = portfolioList.length > 1 || isAll;

  // Brand-tinted pill (element F). Same geometry for desktop; a touch taller
  // for comfortable tap targets on mobile.
  const pill = cn(
    'inline-flex items-center gap-2.5 rounded-full border border-primary/25',
    'bg-(--primary-bg) font-semibold text-foreground transition-colors',
    variant === 'mobile' ? 'h-8 px-3 text-[12px]' : 'h-7 px-[11px] text-[11.5px]',
    className,
  );

  const dot = <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />;

  if (!canSwitch) {
    return (
      <span className={pill} aria-label="Active portfolio">
        {dot}
        <span className="max-w-[42vw] truncate md:max-w-[220px]">{portfolioName}</span>
      </span>
    );
  }

  return (
    <Select value={activePortfolioId} onValueChange={changePortfolio}>
      <SelectTrigger
        aria-label="Switch portfolio"
        className={cn(
          pill,
          'w-auto justify-start shadow-none hover:border-primary/50 focus:ring-2 focus:ring-primary/30 focus:ring-offset-0',
          '[&>svg]:hidden',
        )}
      >
        {dot}
        <span className="max-w-[42vw] truncate md:max-w-[220px]"><SelectValue /></span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </SelectTrigger>
      <SelectContent>
        {portfolioList.map((p) => (
          <SelectItem key={p.id} value={p.id}>{p.name || p.id}</SelectItem>
        ))}
        <SelectItem value={ALL_PORTFOLIOS_ID}>{ALL_PORTFOLIOS_LABEL}</SelectItem>
      </SelectContent>
    </Select>
  );
}
